import { Button, Icon, Notification, Tabs } from '@distribusion/owl-frontend-ui'
import cn from 'classnames'
import { Field, FieldProps, Form, useFormikContext } from 'formik'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'

import Loader from '@components/Loader'
import PassengersDropdown from '@components/PassengersDropdown'
import SearchAnalytics from '@components/SearchForm/Analytics'
import BookingDiscounts from '@components/SearchForm/BookingDiscounts'
import DatesFieldSet from '@components/SearchForm/DatesFieldSet'
import DepartureTimeField from '@components/SearchForm/DepartureTimeField'
import { SearchFormSettings, SearchFormState } from '@components/SearchForm/index'
import LAYOUT_CLASS_NAMES from '@components/SearchForm/layoutClassNames'
import LocationFieldSet from '@components/SearchForm/LocationFieldSet'
import StopoverModalField from '@components/SearchForm/StopOver/ModalField'
import TimePickerSet from '@components/SearchForm/TimePickerSet'
import { DirectionType } from '@components/TripDirection'
import TripDirectionField from '@components/TripDirection/Field'
import useKavanaghIntegration from '@hooks/kavanagh/useKavanaghIntegration'
import useIsMobile from '@hooks/useIsMobile'
import useSearchParams from '@hooks/useSearchParams'
import { FEATURE_FLAGS } from '@lib/analytics/amplitude/plugins/features/featureFlags'
import { useFeatureFlag } from '@lib/analytics/amplitude/plugins/features/hooks/useFeatureFlag'
import fareClassUtils from '@lib/fareClass'
import { useTranslation } from '@lib/i18n'
import paramsUtils from '@lib/params'
import { passengerType } from '@lib/validators'
import { useCarrierLoader } from '@loaders/carrier'
import { useDiscounts } from '@queries/dicounts'
import { useSettings } from '@queries/settings'
import { useParams } from '@stores/params'
import CheckboxField from '@ui/Checkbox/Field'
import { SearchMode } from 'src/api/settings'

const SEARCH_MODE_OPTIONS = [
  { value: 'simple', labelKey: 'searchBar.searchMode.simple' },
  { value: 'advanced', labelKey: 'searchBar.searchMode.advanced' },
] as const

interface FormContentProps extends Directional {
  passengerList: Passenger.Type[]
  settings: SearchFormSettings
  isDepartureDateDisabled?: (date: Date) => boolean
}

const floatingElements: (keyof SearchFormSettings)[] = ['accommodation', 'seatProduct']

const getPassengers = (values: Passenger.Param[], fallback: Passenger.Type): Passenger.Param[] => {
  if (values.length) return values

  return [{ type: fallback.code, maxAge: fallback.maxAge, cards: fallback.cards, pax: 1 }]
}

const FormContent = (props: FormContentProps): ReactElement => {
  const { settings, direction = 'row', isDepartureDateDisabled } = props

  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { payload, isLoading: isButtonTextLoading } = useFeatureFlag(FEATURE_FLAGS.SEARCH_CTA_TEXT)
  const searchBtnText = payload?.text || t('searchBar.search')

  const [
    {
      passengerTypesList,
      weeklyTrip,
      loadConnections,
      openReturn,
      advancedSearch,
      filter: { resetFilters },
    },
  ] = useSettings()
  const { filterJJKTypes, isJJKEnabled } = useKavanaghIntegration()
  const [params] = useParams()
  const { retailerPartnerNumber, locale, currency, marketingCarrierCode } = params
  const { values, setFieldValue, resetForm } = useFormikContext<SearchFormState>()
  const { departureLocation, arrivalLocation, tripDirection, passengers } = values

  const [searchMode, setSearchMode] = useState<SearchMode>(advancedSearch.default)

  // Conditionally use useSearchParams only when Router context is available
  let setSearchParams: ((params: any) => void) | undefined
  try {
    const [, setSearchParamsHook] = useSearchParams()
    setSearchParams = setSearchParamsHook
  } catch {
    // No Router context available (e.g., in SDK), setSearchParams will be undefined
    setSearchParams = undefined
  }

  const discounts = useDiscounts({ enabled: settings.paxDiscountCodes || settings.discountCodes })
  const isReturnTripEnabled = settings.returnTrip
  const isWeeklyTripEnabled = props.settings.weekly ?? weeklyTrip.enabled
  const isOpenReturnTripEnabled = openReturn.enabled
  const showDirection = isReturnTripEnabled || isWeeklyTripEnabled || isOpenReturnTripEnabled

  const isReturnTrip = tripDirection === DirectionType.Return
  const showReturnStartEndTime = isReturnTrip && (settings.returnStartTime || settings.returnEndTime)

  const passengerList = useMemo(() => {
    const tripData = { departure: departureLocation?.code, arrival: arrivalLocation?.code, direction: tripDirection }

    if (isJJKEnabled) return filterJJKTypes(props.passengerList, tripData)

    return props.passengerList
  }, [isJJKEnabled, filterJJKTypes, props.passengerList, departureLocation, arrivalLocation, tripDirection])

  const classNames = useMemo(() => {
    if (direction === 'column') return LAYOUT_CLASS_NAMES[direction]

    const extraFieldsAmount = Object.entries(settings).filter(
      ([key, value]) => value && !floatingElements.includes(key as keyof SearchFormSettings),
    ).length

    if (extraFieldsAmount >= 3) return LAYOUT_CLASS_NAMES['row-2']

    return LAYOUT_CLASS_NAMES.row
  }, [direction, settings])

  const handleValidate = (value: Passenger.Param[]): string | undefined => passengerType(value, retailerPartnerNumber)

  useEffect(() => {
    if (!passengerTypesList.enabled || !isJJKEnabled) return

    const codes = passengerList.map(({ code }) => code)
    const isSame = passengers.every(({ type }) => codes.includes(type))

    if (isSame) return

    const filteredPassengers = passengers.filter(({ type }) => codes.includes(type))

    setFieldValue('passengers', getPassengers(filteredPassengers, passengerList[0]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departureLocation, arrivalLocation, tripDirection])

  const { data: marketingCarrier } = useCarrierLoader({
    marketingCarrierCode: isOpenReturnTripEnabled ? marketingCarrierCode : null,
    params: { locale, currency },
  })

  const handleDirectionTypeChange = (direction: DirectionType): void => {
    const fareClass =
      direction === DirectionType.OpenReturn
        ? fareClassUtils.getOpenReturnFareClass(marketingCarrier?.fareClasses)
        : null

    setFieldValue('fareClass', fareClass ? fareClass?.code : null)
  }

  const shouldShowOpenReturn = useMemo(
    () => isOpenReturnTripEnabled && fareClassUtils.hasOpenReturnFareClass(marketingCarrier?.fareClasses),
    [isOpenReturnTripEnabled, marketingCarrier?.fareClasses],
  )
  const handleReset = (): void => {
    resetForm()
    // Only call setSearchParams if Router context is available
    if (setSearchParams) {
      setSearchParams(paramsUtils.getPersistentUrlParams(params))
    }
  }

  const shouldShowInternationalTripAlert = useMemo(() => {
    if (!passengerTypesList.crossCountry) return false

    return (
      departureLocation && arrivalLocation && departureLocation.code.slice(0, 2) !== arrivalLocation.code.slice(0, 2)
    )
  }, [passengerTypesList.crossCountry, departureLocation, arrivalLocation])

  return (
    <Form id="search-form">
      {shouldShowInternationalTripAlert && (
        <div className="mb-4">
          <Notification disableItemsCenter textAlign="left" type="info">
            {t('searchBar.internationalTripAlert')}
          </Notification>
        </div>
      )}
      <div className={cn('search-form', ...classNames.container)}>
        <div className={cn('search-form__locations', ...classNames.locationsItem)}>
          <LocationFieldSet direction={direction} disabled={settings.disabledLocation} />
        </div>
        {settings.showStopOverFilter && (
          <>
            <div className={cn('search-form__stop-overs', ...classNames.stopOverItem)}>
              <StopoverModalField />
            </div>
          </>
        )}

        {(showDirection || advancedSearch.enabled) && (
          <div className={cn('search-form__trip-direction-radio-group', ...classNames.tripTypeContainer)}>
            {showDirection && (
              <Field
                component={TripDirectionField}
                name="tripDirection"
                twoWay={isReturnTripEnabled}
                weekly={isWeeklyTripEnabled}
                isOpenReturn={shouldShowOpenReturn}
                onChange={handleDirectionTypeChange}
              />
            )}
            {!isMobile && advancedSearch.enabled && (
              <div className="search-form__search-mode-tabs" data-tag="search-mod-tabs-switch">
                <Tabs
                  options={SEARCH_MODE_OPTIONS.map(option => ({
                    value: option.value,
                    label: t(option.labelKey),
                  }))}
                  value={searchMode}
                  onClick={(mode: SearchMode) => setSearchMode(mode)}
                  variant="outlined"
                />
              </div>
            )}
          </div>
        )}

        <div className={cn(...classNames.datePickerContainer)}>
          <DatesFieldSet
            calendarPlacement={settings.calendarPlacement}
            isDepartureDateDisabled={isDepartureDateDisabled}
            searchDays={weeklyTrip.searchDays}
          />
        </div>
        {settings.tripTime && (
          <div className={cn(...classNames.timeContainer)}>
            <DepartureTimeField
              name="departureTime"
              label={t('searchBar.departureTimeLabel')}
              loadConnections={loadConnections?.enabled}
              timeAllowance={loadConnections?.timeAllowance}
            />
          </div>
        )}
        {showReturnStartEndTime && (
          <div className={cn(...classNames.timeContainer)}>
            <TimePickerSet returnStartTime={settings.returnStartTime} returnEndTime={settings.returnEndTime} />
          </div>
        )}
        {settings.passengerType && (
          <div className={cn(...classNames.passengersContainer)}>
            <Field name="passengers" validate={handleValidate}>
              {({ field: { value, name }, form: { setFieldValue, errors } }: FieldProps) => (
                <PassengersDropdown
                  label={t('searchBar.passengers.label')}
                  value={value}
                  onChange={passengers => {
                    setFieldValue(name, passengers)
                  }}
                  availableTypes={passengerTypesList.enabled ? passengerList : null}
                  errors={errors}
                  discounts={discounts}
                  showDiscounts={settings.paxDiscountCodes}
                  showAge={passengerTypesList.multiple}
                />
              )}
            </Field>
          </div>
        )}
        {settings.discountCodes && <BookingDiscounts classNames={classNames} discounts={discounts} />}
        {settings.accommodation && (
          <div
            className={cn(...classNames.addonContainer, 'search-form__accommodation')}
            data-tag="accommodation-container"
          >
            <Field
              component={CheckboxField}
              checked={values.accommodation}
              name="accommodation"
              label={t('searchBar.accommodation')}
              ariaLabel={t('searchBar.accommodation')}
              data-tag="accommodation"
            />
          </div>
        )}
        <div className={cn(...classNames.addonContainer, 'search-form__addon')}>
          {settings.seatProduct && (
            <Field
              component={CheckboxField}
              checked={values.seatProduct}
              name="seatProduct"
              label={t('searchBar.seatProduct')}
              ariaLabel={t('searchBar.seatProduct')}
              data-tag="seat-product"
            />
          )}
          {!isMobile && resetFilters && (
            <div className={'search-form__clear-filters'} data-tag="reset-fields">
              <button type="button" onClick={handleReset} className="search-form__clear-filters-button">
                <Icon name="cross" size="small" className={cn('search-form__clear-filters-icon')} />
                <span>{t('searchBar.reset')}</span>
              </button>
            </div>
          )}
        </div>
        <div className={cn('search-form__submit-button', ...classNames.submitContainer)}>
          <Loader loading={isButtonTextLoading}>
            <Button buttonType="submit">{searchBtnText}</Button>
          </Loader>
        </div>
      </div>
      <SearchAnalytics />
    </Form>
  )
}

export default FormContent
